import { jsx as _jsx } from "react/jsx-runtime";
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';
// Previxio Design System · Button (docs/design/previxio-design-system/Button.dc.html)
// Altezze 36 / 32 / 28, raggio 10 (md) e 8 (sm/xs), peso 500, niente ombre.
// Un solo nome per ogni grafica: niente alias (lo verifica
// design-system-contract.test.ts).
// Added css modifications to make the tooltip component works when the button is disabled:
// https://github.com/shadcn-ui/ui/issues/1022#issuecomment-2671213590
const buttonVariants = cva("cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent text-sm font-medium leading-none transition-[color,background-color,border-color,box-shadow] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:border-brand focus-visible:ring-[3px] focus-visible:ring-brand-soft aria-invalid:focus-visible:ring-0", {
    variants: {
        variant: {
            /** Primario pieno: uno solo per schermata ("Nuovo"). */
            default: 'bg-brand text-white enabled:hover:bg-brand-hover active:bg-brand-hover',
            destructive: 'bg-bad text-white enabled:hover:bg-bad-text active:bg-bad-text',
            /** Secondario del DS: bianco, bordo n-200, testo n-700. */
            outline: 'border-border bg-background text-n-700 enabled:hover:bg-n-50 enabled:hover:text-n-900 aria-invalid:enabled:hover:text-destructive',
            /** dangerSoft del DS. */
            destructive_outline: 'border-border bg-background text-bad-text enabled:hover:border-bad enabled:hover:bg-bad-soft',
            secondary: 'bg-n-100 text-n-900 enabled:hover:bg-n-200 active:bg-n-200',
            ghost: 'text-n-700 enabled:hover:bg-n-100 enabled:hover:text-n-900',
            link: 'text-brand underline-offset-4 enabled:hover:underline',
            /** Azione contestuale (Categorizza / Riconcilia con AI): bordo brand, testo info. */
            'brand-outline': 'border-brand bg-background text-info-text enabled:hover:bg-brand-faint',
            /** proposal del DS: bordo warn, testo warn. */
            proposal: 'border-warn bg-background text-warn-text enabled:hover:bg-warn-soft',
            warning: 'bg-warn text-white enabled:hover:bg-warn-text',
            success: 'bg-good text-white enabled:hover:bg-good-text',
            /** Su superfici scure (BulkBar). */
            dark: 'border-white/20 text-white enabled:hover:bg-white/10 dark:border-black/20 dark:text-n-0 dark:enabled:hover:bg-black/10',
            'dark-fill': 'border-white/20 bg-white/15 text-white enabled:hover:bg-white/25 dark:border-black/20 dark:bg-black/10 dark:text-n-0 dark:enabled:hover:bg-black/20',
        },
        size: {
            default: 'h-9 px-3.5 has-[>svg]:px-3',
            sm: 'h-8 rounded-[8px] px-3 text-[13px] has-[>svg]:px-2.5',
            xs: 'h-7 rounded-[8px] px-2.5 text-xs has-[>svg]:px-2 [&_svg:not([class*="size-"])]:size-3.5',
            lg: 'h-10 px-5 has-[>svg]:px-4',
            icon: 'size-9',
            'icon-sm': 'size-8 rounded-[8px]',
            'icon-xs': 'size-7 rounded-[6px] [&_svg:not([class*="size-"])]:size-3.5',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
});
/**
 * Stato premuto di un bottone interruttore (toggle della toolbar, «Solo
 * miei»): bordo brand, fondo `info-soft`, testo `info-text`, come le chip
 * accese del pannello Filtri. Pensato per `outline` e `ghost`; vince sugli
 * stati di hover della variante.
 */
const PRESSED_CLASS_NAME = 'border-brand bg-info-soft text-info-text enabled:hover:bg-info-soft enabled:hover:text-info-text';
function Button({ className, variant, size, asChild = false, pressed, ...props }) {
    const Comp = asChild ? Slot : 'button';
    const isToggle = pressed !== undefined;
    return (_jsx(Comp, { "data-slot": "button", "aria-pressed": isToggle ? pressed : undefined, "data-pressed": isToggle ? String(pressed) : undefined, className: cn(buttonVariants({ variant, size }), pressed && PRESSED_CLASS_NAME, className), ...props }));
}
export { Button, buttonVariants };
//# sourceMappingURL=button.js.map